import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
// Models
import {
  Instructions,
  Countries,
  AlienNumberPrefixes,
  UsStates,
  RaceOptions,
  EthnicityOptions,
  SexOptions,
} from '../constants';
import { Instruction } from '../models';
import {
  Answers,
  Questions,
  signatureType,
} from 'src/app/shared/models/constants';
import {
  DriverLicense,
  parsePDF417,
  heightConverter,
} from 'src/app/shared/models/drivers-license-parser';
import {
  numberOnly,
  getDay,
  getMonth,
  getYear,
  datePartitioner,
} from 'src/app/shared/functions';

import { Subscription } from 'rxjs';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsnMaskDirective } from 'src/app/shared/directives/ssn-mask.directive';
import {
  BoundBookEntry,
  Form4473,
  Form4473Transferee,
} from 'src/app/shared/models/general';

@Component({
  selector: 'app-buyer-4473',
  templateUrl: './buyer-4473.component.html',
  styleUrls: ['./buyer-4473.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, SsnMaskDirective],
})
export class Buyer4473Component implements AfterViewChecked {
  private destroyRef = inject(DestroyRef);
  @ViewChild('address_county') address_county!: ElementRef<HTMLInputElement>;
  buyer_4473_form: FormGroup;
  subscriptions = new Subscription();
  // icons
  sex_options = SexOptions;
  ethnicity_options = EthnicityOptions;
  race_options = RaceOptions;
  countries = Countries;
  states = UsStates;
  alien_number_prefixes = AlienNumberPrefixes;
  selected_instruction: Instruction = new Instruction();
  instruction_title: string;
  Instructions_body: string;
  form_section: number = 2;
  selected_firearms_for_dispo: BoundBookEntry[] = [];
  parsed_drivers_license: DriverLicense = new DriverLicense();
  scan_drivers_license: boolean = false;

  constructor(
    protected element: ElementRef,
    private fb: FormBuilder,
    public sharedService: SharedService,
  ) {
    // Initializing Form
    this.buyer_4473_form = this.fb.group({
      // Basic Info
      name_first: [null, Validators.required],
      name_middle: [null, Validators.required],
      name_last: [null, Validators.required],

      // Address
      address_street: [null, Validators.required],
      address_zip_code: [null, Validators.required],
      address_city: [null, Validators.required],
      reside_in_city_limits: [null, Validators.required],
      address_state: [null, Validators.required],
      address_county: [null, Validators.required],

      // Birth Place
      birth_country: [null, Validators.required],
      birth_state: [null, Validators.required],
      birth_city: [null, Validators.required],

      // Physical
      height_feet: [
        null,
        [Validators.required, Validators.min(4), Validators.max(7)],
      ],
      height_inches: [
        null,
        [Validators.required, Validators.min(0), Validators.max(11)],
      ],
      weight: [
        null,
        [Validators.required, Validators.min(0), Validators.max(700)],
      ],
      sex: [null, Validators.required],

      // Birth Date
      birth_year: [
        null,
        [Validators.required, Validators.minLength(4), Validators.maxLength(4)],
      ],
      birth_month: [
        null,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.max(12),
        ],
      ],
      birth_day: [
        null,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.max(31),
        ],
      ],

      // Identity
      social_security: [
        null,
        [
          Validators.required,
          Validators.minLength(11),
          Validators.maxLength(11),
        ],
      ],
      upin: [null],

      // Demographics
      ethnicity: [null, Validators.required],
      race: [null, Validators.required],
      citizenship: [null, Validators.required],
      alien_prefix: [null, Validators.required],
      alien_number: [null, Validators.required],

      // Questions
      question_a: [null, Validators.required],
      question_b: [null, Validators.required],
      question_c: [null, Validators.required],
      question_d: [null, Validators.required],
      question_e: [null, Validators.required],
      question_f: [null, Validators.required],
      question_g: [null, Validators.required],
      question_h: [null, Validators.required],
      question_i: [null, Validators.required],
      question_j: [null, Validators.required],
      question_k: [null, Validators.required],
      question_l1: [null, Validators.required],
      question_l2: [null],

      // Certification
      transferee_cert: [false],
      transferee_cert_day: [''],
      transferee_cert_month: [''],
      transferee_cert_year: [''],
      transferee_cert_signature: [''],

      // Recertification
      transferee_recert_day: [''],
      transferee_recert_month: [''],
      transferee_recert_year: [''],
      transferee_recert_signature: [''],
    });
    // yes_no_submit$
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.cancel_4473_form
          ) {
            this.cancelForm();
          } else if (
            response['answer'] === Answers.yes &&
            response['question'] === Questions.reset_4473_form
          ) {
            this.buyer_4473_form.reset();
          }
        }),
    );
    // signatureCaptured$
    this.subscriptions.add(
      this.sharedService.signatureCaptured$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((cert_signature) => {
          this.signatureCaptured(cert_signature.signature);
        }),
    );
    // drivers_license_scanned$
    this.subscriptions.add(
      this.sharedService.drivers_license_scanned$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((parsed_license: DriverLicense) => {
          this.setFocusAddressCounty();
          this.parsed_drivers_license = parsed_license;
          if (this.sharedService.recent_drivers_license.findIndex((x) => x.licenseNumber === parsed_license.licenseNumber) === -1) {
            this.sharedService.saveDriverLicense(parsed_license);
          }
          this.patchScannedDriversLicense();
          this.scan_drivers_license = false;
        }),
    );
    // reset_4473_buyer$
    this.subscriptions.add(
      this.sharedService.reset_4473_buyer$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((reset: boolean) => {
          this.buyer_4473_form.reset();
        }),
    );
  }
  setFocusAddressCounty() {
    this.address_county.nativeElement.focus();
  }
  async signatureCaptured(captured_signature: string) {
    this.mapFormToModel(captured_signature);
    // kiosk_mode
    if (this.sharedService.kiosk_mode) {
      let reserved_4473 = new Form4473Transferee();
      reserved_4473 = this.sharedService.form_4473.transferee;
      this.sharedService.add4473ToReservedList(reserved_4473);
      this.buyer_4473_form.reset();
      this.form_section = 2;
      // pending_4473
    } else if (this.sharedService.pending_4473._id) {
      const transferee_4473 = this.sharedService.form_4473.transferee;
      this.sharedService.pending_4473.transferee = transferee_4473;
      await this.sharedService.updateForm4473(this.sharedService.pending_4473);
      this.sharedService.changeRoute('pending-dispositions');
      // not sure if the route below is needed
    } else {
      this.sharedService.dispo_build_selected_transferee_4473 =
        this.sharedService.form_4473.transferee;
      this.sharedService.changeRoute('seller-form-4473');
    }
  }
  ngOnInit(): void {
    this.sharedService.loading = false;
    this.sharedService.form_4473 = new Form4473();
    this.openQuestionnaireIntro();
    this.gatherFirearmsForDispo();
  }
  mapFormToModel(cert_signature: string) {
    this.sharedService.form_4473.transferee = this.buyer_4473_form.value;
    this.sharedService.form_4473.transferee.transferee_cert_signature =
      cert_signature;
    this.sharedService.form_4473.transferee.transferee_cert_month = getMonth();
    this.sharedService.form_4473.transferee.transferee_cert_day = getDay();
    this.sharedService.form_4473.transferee.transferee_cert_year = getYear();
  }
  gatherFirearmsForDispo() {
    for (const firearm of this.sharedService
      .selected_firearms_for_disposition) {
      this.sharedService.bound_book.filter((entry) => {
        if (entry.firearm_id === firearm.firearm_id) {
          this.selected_firearms_for_dispo.push(entry);
        }
      });
    }
  }

  openQuestionnaireIntro() {
    this.sharedService.openQuestionnaireIntro();
  }

  openInstructions(question_number: string) {
    if (this.selected_instruction?.question === question_number) {
      this.selected_instruction.title = null;
      this.selected_instruction.body = null;
      this.selected_instruction.question = null;
    } else {
      for (const question of Instructions) {
        if (question.question === question_number) {
          this.selected_instruction.title = question.title;
          this.selected_instruction.body = question.body;
          this.selected_instruction.question = question.question;
        }
      }
    }
  }

  closeInstructions() {
    this.selected_instruction.title = null;
    this.selected_instruction.body = null;
  }

  completeQuestionnaireClick() {
    // submit button not currently there
  }
  cancelFormClick() {
    this.cancelForm();
  }
  resetFormClick() {
    this.sharedService.openYesNoModal(Questions.reset_4473_form);
  }
  
  cancelForm() {
    this.sharedService.openUserCredentials('Cancel 4473 Form', 'Return To Form');
  
  }
  previousSectionClick() {
    if (this.form_section > 2) {
      this.form_section--;
    }
  }
  nextSectionClick() {
    this.form_section++;
    if (this.form_section === 3) {
      this.scrollToTop();
    }
  }
  openDigitalSignatureModal() {
    this.sharedService.openDigitalSignature(
      signatureType.find((s) => s.name === 'buyer').name,
    );
  }
  numbersOnly(event: any) {
    return numberOnly(event);
  }
  escapeKioskMode() {
    if (this.sharedService.kiosk_mode) {
      this.sharedService.openUserCredentials('Exit Kiosk Mode', 'Return To Form');
    } else {
      this.sharedService.changeRoute('home');
    }
  }

  patchScannedDriversLicense() {
    let nameMiddle: string;
    if (
      this.parsed_drivers_license.nameMiddle === null ||
      this.parsed_drivers_license.nameMiddle === undefined ||
      this.parsed_drivers_license.nameMiddle === ''
    ) {
      nameMiddle = '"NMN"';
    } else if (this.parsed_drivers_license.nameMiddle.length === 1) {
      nameMiddle = this.parsed_drivers_license.nameMiddle + ' "IO"';
    } else {
      nameMiddle = this.parsed_drivers_license.nameMiddle;
    }
    const scannedSex = this.parsed_drivers_license.gender
      ?.trim()
      .toUpperCase();
    const sex =
      scannedSex === 'M' || scannedSex === 'MALE'
        ? 'Male'
        : scannedSex === 'F' || scannedSex === 'FEMALE'
          ? 'Female'
          : scannedSex === 'X' ||
              scannedSex === 'N' ||
              scannedSex === 'NON-BINARY'
            ? 'Non-Binary'
            : null;
    console.warn('[Buyer 4473] Scanned driver license', {
      license: this.parsed_drivers_license,
      scannedSex,
      selectedSex: sex,
    });
    this.buyer_4473_form.patchValue({
      name_first: this.parsed_drivers_license.nameFirst,
      name_middle: nameMiddle,
      name_last: this.parsed_drivers_license.nameLast,
      address_street: this.parsed_drivers_license.addressStreet,
      address_zip_code: this.parsed_drivers_license.addressPostalCode,
      address_city: this.parsed_drivers_license.addressCity,
      address_state: this.parsed_drivers_license.addressState,

      height_feet: this.parsed_drivers_license.height_ft,
      height_inches: this.parsed_drivers_license.height_in,
      weight: this.parsed_drivers_license.weight,
      sex,
      birth_month: datePartitioner(this.parsed_drivers_license.birthDate, 'm'),
      birth_day: datePartitioner(this.parsed_drivers_license.birthDate, 'd'),
      birth_year: datePartitioner(this.parsed_drivers_license.birthDate, 'y'),
    });
  }

  scanDriversLicense() {
    this.scan_drivers_license = true;
    this.sharedService.openScanIdModal();
  }
  patchFields2() {
    this.buyer_4473_form.patchValue({
      question_a: 0,
      question_b: 0,
      question_c: 0,
      question_d: 0,
      question_e: 0,
      question_f: 0,
      question_g: 0,
      question_h: 0,
      question_i: 0,
      question_j: 0,
      question_k: 0,
      question_l1: 0,
      question_l2: 0,
      buyer_cert: 'John Doe Smith',
    });
  }
  scrollToTop() {
    const container = document.getElementById('scrollable-container');
    if (container) {
      container.scrollTop = 0; // Scroll to the top
    }
  }
  ngAfterViewChecked(): void {
    const controls = this.element.nativeElement.querySelectorAll(
      'input:not([type="hidden"]), textarea, select',
    );
    controls.forEach((control: HTMLInputElement) =>
      control.setAttribute('autocomplete', 'new-password'),
    );
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
